/*
<summary>
   This functional component is all about rendering the Borrower form — a disabled Tool
   Management No. field and an Assign Tag Id input, left blank by default since the borrower
   is always assigning a different tag.
   Developer: Aashish Singh, Created Date: 01-Sep-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Formik, Form as FormikForm, useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import { BorrowToolValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';

export interface IBorrowToolFormValues {
  AssignTagId: string;
}

interface IProps {
  toolManagementNo: string;
  currentTagId: string | null;
  scannedTagId: string;
  isLoading: boolean;
  onModalClosed: () => void;
  submitHandler: (values: IBorrowToolFormValues) => void;
}

const BorrowToolFormContent = (props: IProps) => {
  const { setFieldValue } = useFormikContext<IBorrowToolFormValues>();
  useEffect(() => {
    if (props.scannedTagId?.trim()) {
      setFieldValue('AssignTagId', props.scannedTagId.trim());
    }
  }, [props.scannedTagId, setFieldValue]);
  return (
    <FormikForm className="user-form">
      {props.isLoading && <FormLoader loading={props.isLoading} contained />}
      <Modal.Body>
        <div className="row mb-3">
          <div className="col-12">
            <div className="form-group mb-0">
              <Label
                className="label-style col-form-label"
                label="tool_management_no"
              />
              <input
                className="form-control"
                value={props.toolManagementNo}
                disabled
                readOnly
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="form-group mb-0">
              <Label
                className="label-style col-form-label"
                label="assign_tag_id"
              />
              <Field
                className="form-control"
                name="AssignTagId"
                type="text"
                placeholder="assign_tag_id"
                validcharacter={['-', '_']}
                as={FormikFormInput}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="light-danger"
          onClick={props.onModalClosed}
          disabled={props.isLoading}
        >
          <FormattedMessage id="cancel" />
        </Button>
        <LoaderButton
          type="submit"
          isLoading={props.isLoading}
          text="borrow_tool"
        />
      </Modal.Footer>
    </FormikForm>
  );
};

const BorrowToolForm = (props: IProps) => {
  const initialValues: IBorrowToolFormValues = {
    AssignTagId: '',
  };

  return (
    <Modal
      show={true}
      centered
      size="sm"
      className="borrow-tool-modal"
    >
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage id="borrow_the_tool" />
          <button
            onClick={props.onModalClosed}
            className="Crossicons"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        validationSchema={BorrowToolValidateSchema}
        validateOnBlur={false}
        onSubmit={props.submitHandler}
      >
        <BorrowToolFormContent {...props} />
      </Formik>
    </Modal>
  );
};

export default BorrowToolForm;
